import { ChartContainer } from '@components/chart';
import { AddToReportButton } from '@components/common/AddToReportButton';
import { TextSelectionSearchWrapper } from '@components/common/TextSelectionSearchPopover';
import Scrollbar from '@components/custom-scroll/Scrollbar';
import NoAnalysis from '@components/dynamic-survey/details/NoAnalysis';
import ErrorBoundary from '@components/ErrorBoundary';
import EstimationDialog from '@components/EstimationDialog';
import { TreeMapSVG } from '@components/icon/svg';
import CodeQuotesModal from '@components/interview/Analysis/CodeQuotesModal';
import TreeMapChart from '@components/interview/Analysis/TreeMapChart';
import ThemeUpdateModal from '@components/modal/ThemeUpdateModal';
import Spinner from '@components/spinner/Spinner';
import { useFetchTranscriptList } from '@hooks/getQueries';
import { useAnalyzeInterview } from '@hooks/postQueries';
import { useCheckCredits } from '@hooks/useCheckCredits';
import { Icon } from '@iconify/react';
import { InfoOutlined } from '@mui/icons-material';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import formatString from '@utils/formatString';
import { handleSaveImage } from '@utils/saveChartImage';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import axiosServices from '../../../utils/axios';
import { useGetCodeColorByCategory } from '../Hooks/useGetCodeColorByCategory';
import SankeyChart from './SankeyChart';
import ThemeSelection from './Themes/ThemeSelection';

const AnalysisLanding = ({ canEdit, analysisItemId } = {}) => {
  const { id } = useParams();
  const chartRef = useRef();
  const [value, setValue] = useState('1');
  const [themes, setThemes] = useState([]);
  const [categories, setCategories] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [treeMapData, setTreeMapData] = useState(null);
  const [codesQuotesDict, setCodesQuotesDict] = useState(null);
  const [fetchThemeData, setFetchThemeData] = useState(false);
  const [openThemeModal, setOpenThemeModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [openCodeModal, setOpenCodeModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const { data: transcripts } = useFetchTranscriptList(id);
  const { getColorByCategory, getColorByCodeName } = useGetCodeColorByCategory(id, {
    enabled: themes.length > 0,
  });
  const [openEstimateDialog, setOpenEstimateDialog] = useState(false);
  const [requiredAmtCredit] = useState(0);
  const [creditsNeeded] = useState(null);
  const [processId] = useState(null);

  const { mutate: analyzeInterview } = useAnalyzeInterview(id);
  const { checkCredits, NoCreditDialog } = useCheckCredits(requiredAmtCredit);

  const fetchCodesQuotes = async () => {
    try {
      const storedId = id;
      const cachedData = localStorage.getItem('codes_quotes_dict');
      const parsedCache = cachedData ? JSON.parse(cachedData) : {};

      if (parsedCache[storedId] && Date.now() - parsedCache[storedId].timestamp < 600000) {
        // Use cached data if available and not expired
        setCodesQuotesDict(parsedCache[storedId].data.codes_quotes_dict);
        return;
      }

      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      const url = `${backendUrl}/qualitative-studies/${storedId}/codes_quotes/`;
      const response = await axiosServices.get(url);

      let result = response.data;
      if (typeof result === 'string') {
        result = JSON.parse(result);
      }

      if (result?.codes_quotes_dict) {
        // Cache the result
        const updatedCache = {
          [storedId]: {
            data: result,
            timestamp: Date.now(),
          },
        };
        localStorage.setItem('codes_quotes_dict', JSON.stringify(updatedCache));
        setCodesQuotesDict(result.codes_quotes_dict);
      } else {
        setCodesQuotesDict(null);
      }
    } catch (error) {
      console.error('Error fetching codes quotes:', error);
    }
  };
  useEffect(() => {
    if (id) {
      fetchCodesQuotes();
    }
  }, [id]);

  useEffect(() => {
    if (selectedTheme && categories) {
      const tempData = selectedTheme?.categories_included?.map((el) => ({
        name: el,
        description: categories[el]?.description,
        saliency: categories[el]?.saliency,
        categories_included: categories[el]?.codes_included,
        type: 'theme',
        color: getColorByCategory(el),
      }));
      setTreeMapData(tempData);
    }
  }, [selectedTheme, selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      setTreeMapData([
        {
          name: selectedCategory,
          description: categories[selectedCategory]?.description,
          saliency: categories[selectedCategory]?.saliency,
          categories_included: categories[selectedCategory]?.codes_included,
          type: 'category',
          color: getColorByCategory(selectedCategory),
        },
      ]);
    }
  }, [selectedCategory]);

  const handleRunAnalysis = () => {
    const selectedOption = 'theme';
    checkCredits(
      () => analyzeInterview({ id, processId, analysis_level: selectedOption }),
      creditsNeeded,
    );
  };

  const closeThemeUpdateModal = () => {
    setOpenThemeModal(false);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };
  const handleChipClick = (codeName) => {
    setSelectedCode(codeName);
  };

  const closeModal = () => {
    setSelectedCode(null);
  };

  const onChartClick = (params) => {
    if (!params?.data) return;
    const clickedName = params.data;
    if (clickedName.treeType == 'theme') {
      setSelectedCategory(clickedName.originalName);
    } else if (clickedName.treeType === 'category') {
      return;
    } else {
      let selectedTheme = themes.find((item) => item.name == clickedName.originalName);
      setSelectedTheme(selectedTheme);
      setSelectedCategory(null);
    }
  };
  const fetchThemesAndCategories = async () => {
    const backendUrl = import.meta.env.VITE_BACKEND_URL;
    const url = `${backendUrl}/qualitative-studies/${id}/themes/`;
    setIsLoading(true);

    try {
      const response = await axiosServices.get(url, {
        // Pass analysis_item_id so the right run's themes/categories
        // render when the study has more than one analysis.
        params: analysisItemId ? { analysis_item_id: analysisItemId } : undefined,
      });
      let data = response.data;
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }
      setIsLoading(false);
      setFetchThemeData(false);
      setThemes(data.themes || []);
      setCategories(data.categories || {});
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchThemesAndCategories();
    // analysisItemId changes when the user switches between analyses
    // in the sidebar — refetch so we show the new one's themes.
  }, [fetchThemeData, analysisItemId]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!themes.length && !isLoading) {
    return <NoAnalysis studyId={id} isInterview={true} />;
  }

  const generateThemeReportDescription = (theme, categories_included) => {
    let result = `<div style="margin-bottom: 10px;"><strong>Definition:</strong> ${theme.description || ''}</div>`;

    if (theme.summary) {
      result += `<div style="margin-bottom: 10px;"><strong>Summary:</strong> ${theme.summary}</div>`;
    }

    categories_included?.forEach((category) => {
      const cat = categories[category];
      if (!cat) return;

      result += `
        <hr style="margin-top: 20px; margin-bottom: 10px; border: none; border-top: 1px solid #e8e8e8;" />
        <div style="margin-bottom: 10px;">
          <div style="font-size: 16px;"><strong>Category : ${formatString(category)} (n = ${cat.saliency})</strong> </div>
          <div style="margin-top:10px;"><strong>Definition:</strong> ${cat.description}</div>
          ${cat.summary ? `<div><strong>Summary:</strong> ${cat.summary}</div>` : ''}
          <div>
            <strong>Codes:</strong>
            <p className="my-block" style="margin-top: 5px; display: flex; flex-wrap: wrap; gap: 5px;">
            ${cat.codes_included
              .map(
                (code) => `
                  <span style="
                    background-color: ${getColorByCodeName(code)};
                    color: #3F621A;
                    padding: 3px 6px;
                    border-radius: 4px;
                    font-size: 14px;
                    display: inline-block;
                    margin-right: 5px;
                    margin-bottom: 5px;
                  ">
                    ${code}
                  </span>`,
              )
              .join('')}
            </p>
          </div>
        </div>
      `;
    });

    return result;
  };

  return (
    <Box>
      <TextSelectionSearchWrapper>
        <Card sx={{ padding: 2, borderRadius: 1, marginBottom: '100px' }} variant="outlined">
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6" display={'flex'} alignItems={'center'} gap={'5px'}>
              <BarChartIcon /> Themes
            </Typography>
          </Box>

          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={handleChange}
                aria-label="lab API tabs example"
                sx={{ textTransform: 'none', marginTop: '20px' }}
              >
                <Tab sx={{ textTransform: 'none' }} label="Sankey Chart" value="1" />
                <Tab sx={{ textTransform: 'none' }} label="Treemap & Detailed View" value="3" />
              </TabList>
            </Box>
            <TabPanel value="1" sx={{ padding: '0', mt: 2 }}>
              <Scrollbar>
                <SankeyChart
                  themes={themes}
                  codesQuotesDict={codesQuotesDict}
                  categories={categories}
                  isShowInterpretation={false}
                  isFullScreen={isFullScreen}
                  openThemeModal={openThemeModal}
                  selectedNode={selectedNode}
                  setSelectedNode={setSelectedNode}
                  setOpenThemeModal={setOpenThemeModal}
                  toggleFullScreen={toggleFullScreen}
                  setFetchThemeData={setFetchThemeData}
                  fetchThemesAndCategories={fetchThemesAndCategories}
                  ref={chartRef}
                  getColorByCodeName={getColorByCodeName}
                  getColorByCategory={getColorByCategory}
                  canEdit={canEdit}
                />
              </Scrollbar>
            </TabPanel>

            <TabPanel value="3" sx={{ padding: '0', mt: 2 }}>
              <>
                <ChartContainer
                  hideToolbar={false}
                  title={'Tree Map'}
                  height={500}
                  icon={<TreeMapSVG />}
                  chartRef={chartRef}
                  handleSaveImage={() =>
                    handleSaveImage({ chartRef, fileName: 'Study Completion Time' })
                  }
                >
                  <Box>
                    <Typography mt={1}>
                      The tree map shows themes as top-level blocks. Click a block to drill down
                      into its categories and explore details. Use the reset icon to return to the
                      top-level view.
                    </Typography>
                    <Box display={'flex'} justifyContent={'end'} gap={0.8} alignItems={'center'}>
                      <Box display={'flex'} gap={0.6} alignItems={'center'} mt={1}>
                        {' '}
                        <InfoOutlined fontSize="small" sx={{ color: '#7d5ae8' }} /> You can filter
                        by theme and category
                      </Box>
                      <ThemeSelection
                        selectedTheme={selectedTheme}
                        selectedCategory={selectedCategory}
                        setSelectedTheme={setSelectedTheme}
                        setSelectedCategory={setSelectedCategory}
                        themes={themes}
                      />
                    </Box>

                    <TreeMapChart
                      themes={treeMapData ?? themes}
                      isShowInterpretation={false}
                      ref={chartRef}
                      onChartClick={onChartClick}
                      canEdit={canEdit}
                      isFullScreen={isFullScreen}
                      toggleFullScreen={toggleFullScreen}
                      getColorByCategory={getColorByCategory}
                    />
                  </Box>
                </ChartContainer>
                {selectedTheme === 'all' &&
                  themes.map((theme) => {
                    return (
                      <Card variant="outlined" sx={{ marginTop: '15px' }} key={theme?.name}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              setOpenThemeModal(true);
                              setSelectedNode({
                                name: theme?.name,
                                saliency: theme?.saliency,
                              });
                            }}
                          >
                            <span>Theme : </span> {theme?.name}
                            <Icon icon="lucide:edit" color={'#7d5ae8'} width="22" height="22" />
                          </Typography>
                          <AddToReportButton
                            isShowText={true}
                            name={theme?.name}
                            description={generateThemeReportDescription(
                              theme,
                              theme?.categories_included,
                            )}
                          />
                        </Box>
                        <Typography mt={0.5}>
                          <span style={{ fontWeight: '600' }}>Definition : </span>
                          {theme.description}
                        </Typography>
                        {theme?.summary && (
                          <Typography mt={0.5}>
                            <span style={{ fontWeight: '600' }}>Summary : </span>
                            {theme.summary}
                          </Typography>
                        )}
                        <Typography mt={0.5}>
                          <span style={{ fontWeight: '600' }}>Saliency : </span>
                          {theme?.saliency}
                        </Typography>
                        {theme.categories_included.map((category) => {
                          return (
                            <>
                              <Divider sx={{ marginTop: '10px' }} />
                              <Accordion elevation={0} sx={{ boxShadow: 'none' }} key={category}>
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  aria-controls="panel1-content"
                                  id="panel1-header"
                                >
                                  <Typography component="span">
                                    {' '}
                                    <Typography fontSize={16} fontWeight={'500'}>
                                      <span>Category : </span> {formatString(category)}{' '}
                                      {`(n = ${categories[category]?.saliency})`}
                                    </Typography>
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box key={category}>
                                    <Typography>
                                      <span style={{ fontWeight: '600' }}>Definition : </span>
                                      {categories[category]?.description}
                                    </Typography>
                                    {categories[category]?.summary && (
                                      <Typography mt={0.5}>
                                        <span style={{ fontWeight: '600' }}>Summary : </span>
                                        {categories[category]?.summary}
                                      </Typography>
                                    )}
                                    <Typography>
                                      <span style={{ fontWeight: '600' }}>Saliency : </span>
                                      {categories[category]?.saliency}
                                    </Typography>

                                    <Box display={'flex'} gap={1} mt={1} flexWrap={'wrap'}>
                                      {categories[category]?.codes_included.map((item) => (
                                        <Typography
                                          sx={{
                                            backgroundColor: getColorByCategory(category),
                                            color: '#3F621A',
                                            padding: '5px 10px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                          }}
                                          key={item}
                                          onClick={() =>
                                            handleChipClick({
                                              name: item,
                                              definition: categories[category]?.description,
                                              categoryKey: category,
                                              location: 0,
                                            })
                                          }
                                        >
                                          {item}
                                        </Typography>
                                      ))}
                                    </Box>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            </>
                          );
                        })}
                      </Card>
                    );
                  })}
                {selectedTheme !== 'all' && (
                  <>
                    <Card variant="outlined" sx={{ marginTop: '15px' }}>
                      <Box sx={{ padding: '0px' }}>
                        <Box
                          display={'flex'}
                          justifyContent={'space-between'}
                          alignItems={'center'}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              setOpenThemeModal(true);
                              setSelectedNode({
                                name: selectedTheme?.name,
                                saliency: selectedTheme?.saliency,
                              });
                            }}
                          >
                            <span>Theme : </span> {selectedTheme?.name}{' '}
                            <Icon icon="lucide:edit" color={'#7d5ae8'} width="22" height="22" />
                          </Typography>
                          <AddToReportButton
                            isShowText={true}
                            name={selectedTheme?.name}
                            description={generateThemeReportDescription(
                              selectedTheme,
                              selectedTheme?.categories_included,
                            )}
                          />
                        </Box>
                        <Typography mt={0.5}>
                          <span style={{ fontWeight: '600' }}>Definition : </span>
                          {selectedTheme?.description}
                        </Typography>

                        {selectedTheme?.summary && (
                          <Typography mt={0.5}>
                            <span style={{ fontWeight: '600' }}>Summary : </span>
                            {selectedTheme?.summary}
                          </Typography>
                        )}
                        <Typography mt={0.5}>
                          <span style={{ fontWeight: '600' }}>Saliency : </span>
                          {selectedTheme?.saliency}
                        </Typography>
                      </Box>
                      <Divider sx={{ marginTop: '10px' }} />
                      {!selectedCategory &&
                        selectedTheme?.categories_included?.map((category) => {
                          return (
                            <Accordion elevation={0} sx={{ boxShadow: 'none' }} key={category}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                              >
                                <Typography component="span">
                                  {' '}
                                  <Typography fontSize={16} fontWeight={'500'}>
                                    <span>Category : </span> {formatString(category)}{' '}
                                    {`(n = ${categories[category]?.saliency})`}
                                  </Typography>
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box key={category}>
                                  <Typography>
                                    <span style={{ fontWeight: '600' }}>Definition : </span>
                                    {categories[category]?.description}
                                  </Typography>
                                  {categories[category]?.summary && (
                                    <Typography mt={0.5}>
                                      <span style={{ fontWeight: '600' }}>Summary : </span>
                                      {categories[category]?.summary}
                                    </Typography>
                                  )}
                                  <Typography>
                                    <span style={{ fontWeight: '600' }}>Saliency : </span>
                                    {categories[category]?.saliency}
                                  </Typography>

                                  <Box display={'flex'} gap={1} mt={1} flexWrap={'wrap'}>
                                    {categories[category]?.codes_included.map((item) => (
                                      <Typography
                                        sx={{
                                          backgroundColor: getColorByCategory(category),
                                          color: '#3F621A',
                                          padding: '5px 10px',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                        }}
                                        key={item}
                                        onClick={() =>
                                          handleChipClick({
                                            name: item,
                                            definition: categories[category]?.description,
                                            categoryKey: category,
                                            location: 0,
                                          })
                                        }
                                      >
                                        {item}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      {selectedCategory && (
                        <Box>
                          <Typography fontSize={16} fontWeight={'500'} mt={1}>
                            <span>Category : </span> {formatString(selectedCategory)}{' '}
                            {`(n = ${categories[selectedCategory]?.saliency})`}
                          </Typography>
                          <Typography mt={0.5}>
                            <span style={{ fontWeight: '600' }}>Definition : </span>
                            {categories[selectedCategory]?.description}
                          </Typography>
                          {categories[selectedCategory]?.summary && (
                            <Typography mt={0.5}>
                              <span style={{ fontWeight: '600' }}>Summary : </span>
                              {categories[selectedCategory]?.summary}
                            </Typography>
                          )}
                          <Typography mt={0.5}>
                            <span style={{ fontWeight: '600' }}>Saliency : </span>
                            {categories[selectedCategory]?.saliency}
                          </Typography>
                          <Box display={'flex'} gap={1} mt={1} flexWrap={'wrap'}>
                            {categories[selectedCategory]?.codes_included.map((item) => (
                              <Typography
                                sx={{
                                  backgroundColor: getColorByCategory(selectedCategory),
                                  color: '#3F621A',
                                  padding: '5px 10px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                                key={item}
                                onClick={() =>
                                  handleChipClick({
                                    name: item,
                                    definition: categories[selectedCategory]?.description,
                                    categoryKey: selectedCategory,
                                    location: 0,
                                  })
                                }
                              >
                                {item}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Card>
                  </>
                )}
              </>
            </TabPanel>
          </TabContext>
          {selectedCode && (
            <Dialog
              open={selectedCode ? true : false}
              onClose={closeModal}
              size="md"
              maxWidth="800px"
            >
              <DialogTitle>
                <Typography variant="h6">Code Details</Typography>
              </DialogTitle>
              <IconButton
                onClick={closeModal}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
              <Divider />
              <DialogContent
                sx={{
                  maxWidth: '90vw',
                  minWidth: '600px',
                  position: 'relative',
                  padding: '0',
                }}
              >
                <ErrorBoundary>
                  <Suspense fallback={<Spinner />}>
                    <CodeQuotesModal
                      transcripts={transcripts}
                      open={selectedCode ? true : false}
                      closeModal={closeModal}
                      studyId={id}
                      setOldCodesQuotesDict={setCodesQuotesDict}
                      openCode={selectedCode}
                      setSelectedCode={setSelectedCode}
                      setFetchThemeData={setFetchThemeData}
                      canEdit={canEdit}
                    />
                  </Suspense>
                </ErrorBoundary>
              </DialogContent>
            </Dialog>
          )}

          <ThemeUpdateModal
            openThemeModal={openThemeModal}
            closeCategoryModal={closeThemeUpdateModal}
            themes={themes}
            fetchThemesAndCategories={fetchThemesAndCategories}
            selectedNode={selectedNode}
            setOpenCodeModal={setOpenCodeModal}
            openCodeModal={openCodeModal}
            setSelectedCode={setSelectedCode}
            categories={categories}
            id={id}
            getColorByCategory={getColorByCategory}
          />
          <NoCreditDialog />
          <EstimationDialog
            open={openEstimateDialog}
            onClose={() => setOpenEstimateDialog(false)}
            onAgree={handleRunAnalysis}
            description="This will incur additional entitlements."
            title={'This action requires additional entitlements.'}
          />
        </Card>
      </TextSelectionSearchWrapper>
    </Box>
  );
};

export default AnalysisLanding;
